// =============================================================
// mapLayers.js — Basemap + overlay catalog
// -------------------------------------------------------------
// Declarative registry of every map layer the platform knows about.
// Consumed by:
//   • utils/mapLayerEngine.js          – applies layers to a Mapbox GL map
//   • components/shared/MapLayerCatalog – renders the categorized UI
//
// `status` controls behavior:
//   'live'    – wired to a real, stable public data source; usable now
//   'planned' – shown (greyed out) so the full requested taxonomy is visible.
//               Needs a data source / licensing decision before it can be wired.
//               Most of these are FeatureServer / proprietary datasets that
//               require server-side fetching or paid licensing (onX / HuntStand
//               style hunting data, Google tiles, etc.).
//
// Source `type`:
//   'mapbox-style' – swap the whole Mapbox GL style (basemaps only)
//   'raster-xyz'   – {z}/{x}/{y} raster tile template (basemap or overlay)
//   'raster-wms'   – OGC WMS GetMap; uses Mapbox GL's {bbox-epsg-3857} token
//   'terrain'      – Mapbox terrain-DEM driven 3D (handled by a dedicated control)
//
// NOTE on `tier` (planned layers only): 2 = public source exists, needs wiring;
// 3 = proprietary / licensed / no clean public source (research required).
// =============================================================

// ── Templated overlay URL builders ───────────────────────────
// Mapbox GL substitutes {bbox-epsg-3857} (minx,miny,maxx,maxy) per raster tile.

// ArcGIS dynamic MapServer "export" → transparent PNG. Omitting `layers`
// renders the service's default sublayers, which avoids depending on fragile
// per-sublayer IDs that differ between service versions.
const arcgisExport = (root, layers) =>
  `${root}/export?bbox={bbox-epsg-3857}&bboxSR=3857&imageSR=3857&size=256,256&dpi=96&format=png32&transparent=true&f=image` +
  (layers != null ? `&layers=show:${layers}` : '');

// OGC WMS 1.1.1 GetMap (EPSG:3857). Used for services that aren't ArcGIS REST.
const wms111 = (base, layers) =>
  `${base}?service=WMS&version=1.1.1&request=GetMap&layers=${layers}&styles=&format=image/png&transparent=true&srs=EPSG:3857&width=256&height=256&bbox={bbox-epsg-3857}`;

// ── Basemaps (exactly one active at a time) ──────────────────
export const BASEMAPS = [
  // -- Live (stable public tile services / Mapbox styles) --
  {
    id: 'mapbox-hybrid', label: 'Hybrid (Satellite + Roads)', status: 'live',
    type: 'mapbox-style', style: 'mapbox://styles/mapbox/satellite-streets-v12',
    group: 'Aerial',
  },
  {
    id: 'mapbox-aerial', label: 'Hi-Res Aerial', status: 'live',
    type: 'mapbox-style', style: 'mapbox://styles/mapbox/satellite-v9',
    group: 'Aerial',
  },
  {
    id: 'esri-imagery', label: 'Aerial (Esri World Imagery)', status: 'live',
    type: 'raster-xyz',
    tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'],
    attribution: 'Imagery © Esri, Maxar, Earthstar Geographics', group: 'Aerial',
  },
  {
    id: 'usgs-imagery', label: 'USGS Imagery', status: 'live',
    type: 'raster-xyz',
    tiles: ['https://basemap.nationalmap.gov/arcgis/rest/services/USGSImageryOnly/MapServer/tile/{z}/{y}/{x}'],
    attribution: 'USGS The National Map', group: 'Aerial',
  },
  {
    id: 'usgs-imagery-topo', label: 'USGS Imagery + Topo', status: 'live',
    type: 'raster-xyz',
    tiles: ['https://basemap.nationalmap.gov/arcgis/rest/services/USGSImageryTopo/MapServer/tile/{z}/{y}/{x}'],
    attribution: 'USGS The National Map', group: 'Topo',
  },
  {
    id: 'usgs-topo', label: 'USGS Topo (Modern)', status: 'live',
    type: 'raster-xyz',
    tiles: ['https://basemap.nationalmap.gov/arcgis/rest/services/USGSTopo/MapServer/tile/{z}/{y}/{x}'],
    attribution: 'USGS The National Map', group: 'Topo',
  },
  {
    id: 'opentopo', label: 'Topography (OpenTopoMap)', status: 'live',
    type: 'raster-xyz',
    tiles: [
      'https://a.tile.opentopomap.org/{z}/{x}/{y}.png',
      'https://b.tile.opentopomap.org/{z}/{x}/{y}.png',
      'https://c.tile.opentopomap.org/{z}/{x}/{y}.png',
    ],
    maxzoom: 17,
    attribution: 'Map data: © OpenStreetMap contributors, SRTM | © OpenTopoMap (CC-BY-SA)',
    group: 'Topo',
  },
  {
    id: 'mapbox-outdoors', label: 'Topographic (Outdoors)', status: 'live',
    type: 'mapbox-style', style: 'mapbox://styles/mapbox/outdoors-v12', group: 'Topo',
  },
  {
    id: 'mapbox-streets', label: 'Street Map', status: 'live',
    type: 'mapbox-style', style: 'mapbox://styles/mapbox/streets-v12', group: 'Street',
  },
  {
    id: 'osm', label: 'OpenStreetMap', status: 'live',
    type: 'raster-xyz',
    tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
    maxzoom: 19,
    attribution: '© OpenStreetMap contributors', group: 'Street',
  },

  // -- Planned (data source / licensing pending) --
  { id: 'usgs-vintage', label: 'Vintage USGS (Historical Topo)', status: 'planned', tier: 2,
    note: 'USGS topoView historical quad scans — collar-clipped GeoPDF/GeoTIFF, needs a tiling step.' },
  { id: 'esri-wayback', label: 'Historical Satellite View', status: 'planned', tier: 2,
    note: 'Esri Wayback imagery archive — per-date tile layers selectable by capture date.' },
  { id: 'leaf-off', label: 'Leaf-Off Imagery', status: 'planned', tier: 2,
    note: 'State/regional spring leaf-off orthoimagery — source varies by state.' },
  { id: 'recent-imagery', label: 'Recent Imagery (Not Hi-Res)', status: 'planned', tier: 2,
    note: 'Sentinel-2 / Planet recent capture — needs a cloud-filtered mosaic source.' },
  { id: 'infrared', label: 'Infrared (CIR)', status: 'planned', tier: 2,
    note: 'NAIP color-infrared or Sentinel false-color — vegetation/health analysis.' },
  { id: 'google-maps', label: 'Google Maps / Street Roads', status: 'planned', tier: 3,
    note: 'Embedding Google tiles in Mapbox violates Google ToS — requires the Google Maps JS API + billing.' },
  { id: 'street-view', label: 'Street View', status: 'planned', tier: 3,
    note: 'Requires Google Street View API (paid key) embedded separately from the Mapbox canvas.' },
];

// ── Overlay categories (mirror the requested taxonomy) ───────
// Each layer: { id, label, status, type?, url?/tiles?, attribution?, tier?, note?, defaultOpacity? }
export const OVERLAY_CATEGORIES = [
  {
    id: 'universal', label: 'Universal',
    layers: [
      {
        id: 'wetlands', label: 'Wetlands (USFWS NWI)', status: 'live', type: 'raster-wms',
        url: 'https://www.fws.gov/wetlandsmapservice/services/Wetlands/MapServer/WMSServer?service=WMS&request=GetMap&version=1.3.0&layers=1&styles=&format=image/png&transparent=true&crs=EPSG:3857&width=256&height=256&bbox={bbox-epsg-3857}',
        attribution: 'U.S. Fish & Wildlife Service — National Wetlands Inventory',
        defaultOpacity: 0.75,
      },
      {
        id: 'government-land', label: 'Government Units & Federal Land (USGS)', status: 'live',
        url: arcgisExport('https://carto.nationalmap.gov/arcgis/rest/services/govunits/MapServer'),
        attribution: 'USGS The National Map — Government Units', defaultOpacity: 0.55,
      },
      {
        id: 'contours', label: 'Contours (USGS)', status: 'live',
        url: arcgisExport('https://carto.nationalmap.gov/arcgis/rest/services/contours/MapServer'),
        attribution: 'USGS The National Map — Elevation Contours', defaultOpacity: 0.7,
      },
      {
        id: 'surface-water', label: 'Surface Water / Streams / Rivers (USGS NHD)', status: 'live',
        url: arcgisExport('https://hydro.nationalmap.gov/arcgis/rest/services/nhd/MapServer'),
        attribution: 'USGS National Hydrography Dataset', defaultOpacity: 0.85,
      },
      { id: 'parcels-private', label: 'Private Land (Parcels)', status: 'planned', tier: 2,
        note: 'Already available on the parcel map via Regrid; surfacing here is wiring-only.' },
      { id: 'soil-capability', label: 'Soil Capability', status: 'planned', tier: 2,
        note: 'USDA NRCS SSURGO soil survey — capability class WMS.' },
    ],
  },
  {
    id: 'current-conditions', label: 'Current Conditions',
    layers: [
      { id: 'active-wildfire', label: 'Active Wildfire', status: 'planned', tier: 2,
        note: 'NIFC / IRWIN active fire perimeters (FeatureServer — needs server fetch).' },
      { id: 'smoke-forecast', label: 'Smoke Forecast', status: 'planned', tier: 2,
        note: 'NOAA HRRR-Smoke forecast WMS.' },
      { id: 'drought', label: 'Drought Data', status: 'planned', tier: 2,
        note: 'U.S. Drought Monitor (UNL/NDMC) weekly WMS.' },
      { id: 'air-quality', label: 'Air Quality', status: 'planned', tier: 2,
        note: 'EPA AirNow AQI tiles / API.' },
      { id: 'precip-radar', label: 'Precipitation Radar', status: 'planned', tier: 2,
        note: 'NOAA MRMS or RainViewer time-indexed radar tiles.' },
      { id: 'weather-temp', label: 'Weather / Temperature', status: 'planned', tier: 2,
        note: 'NWS / OpenWeather temperature raster.' },
      { id: 'cwd-counties', label: 'CWD Positive Counties', status: 'planned', tier: 3,
        note: 'Chronic Wasting Disease — fragmented per-state agency data; no unified feed.' },
      { id: 'cwd-zones', label: 'CWD Zones & Locations', status: 'planned', tier: 3,
        note: 'Per-state management-zone boundaries; manual aggregation required.' },
      { id: 'cell-coverage', label: 'Cell Coverage', status: 'planned', tier: 3,
        note: 'FCC Broadband Map / carrier data — coverage is proprietary per carrier.' },
    ],
  },
  {
    id: 'land-use-access', label: 'Land Use & Access',
    layers: [
      { id: 'opportunity-zones', label: 'Opportunity Zones', status: 'planned', tier: 2,
        note: 'CDFI Fund / Census designated QOZ tracts — public WMS available.' },
      { id: 'wilderness', label: 'Wilderness Areas', status: 'planned', tier: 2, note: 'USGS PAD-US / Wilderness.net.' },
      { id: 'nca', label: 'National Conservation Areas', status: 'planned', tier: 2, note: 'BLM NLCS dataset.' },
      { id: 'usfws-areas', label: 'USFWS Areas', status: 'planned', tier: 2, note: 'USFWS National Realty / refuge boundaries.' },
      { id: 'slope', label: 'Slope (Angle & Aspect)', status: 'planned', tier: 2, note: 'Derived from 3DEP DEM (client or tile-side hillshade math).' },
      { id: 'sections-gis', label: 'Sections (GIS)', status: 'planned', tier: 2, note: 'BLM PLSS section grid.' },
      { id: 'public-access-easements', label: 'Public Access Easements', status: 'planned', tier: 3, note: 'State-by-state; often not centrally published.' },
      { id: 'ducks-unlimited', label: 'Ducks Unlimited', status: 'planned', tier: 3, note: 'DU project data — partnership/licensing required.' },
      { id: 'grazing-allotments', label: 'Grazing Allotments', status: 'planned', tier: 2, note: 'BLM grazing allotment boundaries.' },
      { id: 'great-lakes-bathymetry', label: 'Great Lakes Bathymetry', status: 'planned', tier: 2, note: 'NOAA Great Lakes bathymetric grids.' },
      { id: 'conservation-easements', label: 'Conservation Easements', status: 'planned', tier: 2, note: 'NCED (National Conservation Easement Database).' },
      { id: 'land-use', label: 'Land Use', status: 'planned', tier: 2, note: 'USGS NLCD land-cover classification.' },
    ],
  },
  {
    id: 'services-government', label: 'Services / Government Data',
    layers: [
      {
        id: 'fema-floodplain', label: 'FEMA Floodplain (NFHL)', status: 'live', type: 'raster-wms',
        url: 'https://hazards.fema.gov/gis/nfhl/services/public/NFHL/MapServer/WMSServer?service=WMS&request=GetMap&version=1.3.0&layers=28&styles=&format=image/png&transparent=true&crs=EPSG:3857&width=256&height=256&bbox={bbox-epsg-3857}',
        attribution: 'FEMA National Flood Hazard Layer',
        defaultOpacity: 0.6,
      },
      { id: 'fema-panels', label: 'FEMA Panels & LOMRs', status: 'planned', tier: 2, note: 'NFHL FIRM panel index + Letters of Map Revision (NFHL sublayers).' },
      {
        id: 'huc8', label: 'Watershed Boundaries / HUCs (USGS WBD)', status: 'live',
        url: arcgisExport('https://hydro.nationalmap.gov/arcgis/rest/services/wbd/MapServer'),
        attribution: 'USGS Watershed Boundary Dataset', defaultOpacity: 0.6,
      },
      {
        id: 'structures', label: 'Public Structures — Schools, Hospitals, etc. (USGS)', status: 'live',
        url: arcgisExport('https://carto.nationalmap.gov/arcgis/rest/services/structures/MapServer'),
        attribution: 'USGS The National Map — Structures', defaultOpacity: 0.9,
      },
      {
        id: 'geonames', label: 'Named Features — Summits & Gaps (USGS GNIS)', status: 'live',
        url: arcgisExport('https://carto.nationalmap.gov/arcgis/rest/services/geonames/MapServer'),
        attribution: 'USGS Geographic Names Information System', defaultOpacity: 0.9,
      },
      { id: 'airports', label: 'Airports', status: 'planned', tier: 2, note: 'FAA / BTS — included in the USGS Transportation overlay (Roads, Trails category).' },
      { id: 'city-limits', label: 'City Limits', status: 'planned', tier: 2, note: 'Census Places — boundaries also shown by the Government Units overlay (Universal).' },
      { id: 'electric-service', label: 'Electric Service Area', status: 'planned', tier: 2, note: 'HIFLD electric retail service territories.' },
      { id: 'iso', label: 'Independent System Operators', status: 'planned', tier: 2, note: 'HIFLD ISO/RTO regions.' },
      { id: 'solar-farms', label: 'Solar Farms', status: 'planned', tier: 2, note: 'EIA / HIFLD utility-scale solar.' },
      { id: 'power-plants', label: 'Power Plants', status: 'planned', tier: 2, note: 'EIA power plant points.' },
      { id: 'oil-wells', label: 'Oil Wells', status: 'planned', tier: 3, note: 'State O&G commissions — fragmented per state.' },
      { id: 'fiber', label: 'Fiber Optic Service', status: 'planned', tier: 3, note: 'FCC Broadband Map — provider-reported.' },
      { id: 'fsa-clu', label: 'FSA CLU', status: 'planned', tier: 3, note: 'USDA Common Land Units — access restricted.' },
      { id: 'gas-service', label: 'Gas Service Areas', status: 'planned', tier: 2, note: 'HIFLD natural gas service territories.' },
      { id: 'housing-developments', label: 'Housing Developments', status: 'planned', tier: 3, note: 'No single national source.' },
      { id: 'plss', label: 'PLSS (GIS Grid)', status: 'planned', tier: 2, note: 'BLM CadNSDI PLSS township/range/section.' },
      { id: 'aquifers', label: 'Principal Aquifers', status: 'planned', tier: 2, note: 'USGS principal aquifers (well-depth would need NWIS join).' },
      { id: 'schools-secondary', label: 'Secondary Schools', status: 'planned', tier: 2, note: 'HIFLD / NCES school points.' },
      { id: 'schools-elementary', label: 'Elementary Schools', status: 'planned', tier: 2, note: 'HIFLD / NCES school points.' },
      { id: 'schools-unified', label: 'Unified Schools', status: 'planned', tier: 2, note: 'NCES unified district boundaries.' },
      { id: 'sewer-service', label: 'Sewer Service Areas', status: 'planned', tier: 3, note: 'Local utility data — not centrally published.' },
      { id: 'substations', label: 'Substations', status: 'planned', tier: 2, note: 'HIFLD electric substations.' },
      { id: 'summits-gaps', label: 'Summits and Gaps', status: 'planned', tier: 2, note: 'Shown by the "Named Features (GNIS)" overlay above; standalone styled layer pending.' },
      { id: 'water-service', label: 'Water Service Areas', status: 'planned', tier: 3, note: 'Local utility data — fragmented.' },
      { id: 'wind-farms', label: 'Wind Farms', status: 'planned', tier: 2, note: 'USGS US Wind Turbine Database (project polygons).' },
      { id: 'wind-turbines', label: 'Wind Turbines (Permitted)', status: 'planned', tier: 2, note: 'USGS USWTDB turbine points + FAA permits.' },
      { id: 'zcta', label: 'Zip Code Tabulation', status: 'planned', tier: 2, note: 'Census ZCTA boundaries.' },
    ],
  },
  {
    id: 'roads-trails', label: 'Roads, Trails, Etc.',
    layers: [
      {
        id: 'usgs-transportation', label: 'Roads, Rail, Trails & Airports (USGS)', status: 'live',
        url: arcgisExport('https://carto.nationalmap.gov/arcgis/rest/services/transportation/MapServer'),
        attribution: 'USGS The National Map — Transportation', defaultOpacity: 0.9,
      },
      { id: 'railroads', label: 'Railroads', status: 'planned', tier: 2, note: 'Included in the USGS Transportation overlay above; standalone styled layer pending (BTS/HIFLD).' },
      { id: 'roadless-areas', label: 'Roadless Areas', status: 'planned', tier: 2, note: 'USFS Inventoried Roadless Areas.' },
      { id: 'forest-visitor-maps', label: 'Forest Visitor Maps', status: 'planned', tier: 2, note: 'USFS FSTopo / visitor map tiles.' },
      { id: 'poi', label: 'Points of Interest', status: 'planned', tier: 2, note: 'Available via Mapbox/OSM POI layers.' },
      { id: 'trail-slope', label: 'Trail Slope', status: 'planned', tier: 2, note: 'Derive from trail vectors + DEM.' },
      { id: 'trail-mileages', label: 'Trail Mileages', status: 'planned', tier: 2, note: 'USFS/USGS trail vectors with length labels.' },
    ],
  },
  {
    id: 'trees-crops-soil', label: 'Trees, Crops, Soil',
    layers: [
      {
        id: 'crop-distribution', label: 'US Crop Distribution (USDA CropScape)', status: 'live',
        url: wms111('https://nassgeodata.gmu.edu/CropScapeService/wms_cdlall.cgi', 'cdl_2024'),
        attribution: 'USDA NASS Cropland Data Layer (CropScape)', defaultOpacity: 0.7,
      },
      { id: 'soil-overlay', label: 'Soil Overlay (type & usefulness)', status: 'planned', tier: 2, note: 'USDA NRCS SSURGO soil survey WMS.' },
      { id: 'historic-wildfire', label: 'Historic Wildfire', status: 'planned', tier: 2, note: 'MTBS / NIFC historical fire perimeters.' },
      { id: 'timber-cuts', label: 'Timber Cuts', status: 'planned', tier: 2, note: 'USFS harvest activity (FACTS) polygons.' },
      { id: 'coniferous-dist', label: 'Coniferous Tree Distribution', status: 'planned', tier: 2, note: 'USFS Forest Type / LANDFIRE EVT.' },
      { id: 'deciduous-dist', label: 'Deciduous Tree Distribution', status: 'planned', tier: 2, note: 'USFS Forest Type / LANDFIRE EVT.' },
      { id: 'decid-vs-conif', label: 'Deciduous vs. Coniferous', status: 'planned', tier: 2, note: 'Reclassified LANDFIRE EVT.' },
      { id: 'young-aspen', label: 'Young Aspen Forests', status: 'planned', tier: 3, note: 'Requires age-class modeling from forest inventory.' },
      { id: 'acorn-oaks', label: 'Acorn-Producing Oaks', status: 'planned', tier: 3, note: 'Proprietary habitat modeling (onX/HuntStand style).' },
      { id: 'thermal-cover', label: 'Thermal Deer Cover', status: 'planned', tier: 3, note: 'Proprietary habitat modeling.' },
    ],
  },
  {
    id: 'wildlife', label: 'Wildlife',
    layers: [
      { id: 'southern-rut', label: 'Southern Rut Map', status: 'planned', tier: 3, note: 'Proprietary (onX/HuntStand) modeled rut timing.' },
      { id: 'bc-whitetail-trend', label: 'B&C Whitetail Trend', status: 'planned', tier: 3, note: 'Boone & Crockett records — licensed dataset.' },
      { id: 'elk-distribution', label: 'Elk Distribution Map', status: 'planned', tier: 3, note: 'State wildlife agency range maps — fragmented.' },
      { id: 'mule-deer-distribution', label: 'Mule Deer Distribution Map', status: 'planned', tier: 3, note: 'State agency range maps — fragmented.' },
      { id: 'elk-bc-records', label: 'Elk B&C Records (Last 10 Yrs)', status: 'planned', tier: 3, note: 'B&C records — licensed.' },
      { id: 'pronghorn-bc-records', label: 'Pronghorn B&C Records (Last 10 Yrs)', status: 'planned', tier: 3, note: 'B&C records — licensed.' },
      { id: 'turkey-subspecies', label: 'Turkey Sub-Species', status: 'planned', tier: 3, note: 'NWTF subspecies range — partnership/licensing.' },
      { id: 'bc-trophy-biggame', label: 'B&C Trophy Big Game', status: 'planned', tier: 3, note: 'Boone & Crockett records — licensed.' },
    ],
  },
];

// ── Lookups + derived helpers ────────────────────────────────
export const BASEMAP_BY_ID = Object.fromEntries(BASEMAPS.map(b => [b.id, b]));

export const ALL_OVERLAYS = OVERLAY_CATEGORIES.flatMap(c => c.layers);
export const OVERLAY_BY_ID = Object.fromEntries(ALL_OVERLAYS.map(l => [l.id, l]));

export const DEFAULT_BASEMAP_ID = 'mapbox-hybrid';

// Count of live vs planned, handy for the catalog header.
export const LAYER_STATS = {
  basemapsLive:  BASEMAPS.filter(b => b.status === 'live').length,
  basemapsTotal: BASEMAPS.length,
  overlaysLive:  ALL_OVERLAYS.filter(l => l.status === 'live').length,
  overlaysTotal: ALL_OVERLAYS.length,
};
